import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { Box, useMediaQuery, useTheme, Typography } from '@mui/material';

interface Point {
  x: number;
  y: number;
  intensity?: number;
  distortionRadius?: number; // Add distortion radius for line curving
}

interface AmslerGridTestProps {
  onMarkPoint: (point: Point) => void;
  markedPoints: Point[];
  eye: 'left' | 'right';
}

// Standard Amsler grid size - 400x400px is the standard medical size
const STANDARD_GRID_SIZE = 400; // Back to standard size
const GRID_PADDING = 0; // No padding

const AmslerGridTest: React.FC<AmslerGridTestProps> = ({ onMarkPoint, markedPoints, eye }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isPressing, setIsPressing] = useState(false);
  const [pressPosition, setPressPosition] = useState<{ x: number, y: number } | null>(null);
  const [pressStartTime, setPressStartTime] = useState<number>(0);
  const [currentIntensity, setCurrentIntensity] = useState<number>(0);
  const [currentDistortionRadius, setCurrentDistortionRadius] = useState<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const [canvasScale, setCanvasScale] = useState(1);
  
  // Use Material-UI's responsive breakpoints
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMdScreen = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  // Calculate responsive scale based on screen size
  useLayoutEffect(() => {
    const calculateScale = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const smallestDimension = Math.min(viewportWidth, viewportHeight);
      
      // On small screens, make the grid larger (up to 100% of viewport)
      if (isXsScreen) {
        return Math.min(0.95 * smallestDimension / STANDARD_GRID_SIZE, 1.5);
      } 
      // On medium screens, make the grid slightly larger
      else if (isSmScreen) {
        return Math.min(0.85 * smallestDimension / STANDARD_GRID_SIZE, 1.3);
      }
      // On larger screens, keep it at a reasonable size
      else {
        return Math.min(0.75 * smallestDimension / STANDARD_GRID_SIZE, 1.2);
      }
    };
    
    setCanvasScale(calculateScale());
    
    // Update scale when window resizes
    const handleResize = () => {
      setCanvasScale(calculateScale());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isXsScreen, isSmScreen, isMdScreen]);
  
  // Function to create distorted grid lines
  const createDistortedGrid = (
    ctx: CanvasRenderingContext2D, 
    gridLines: number, 
    cellSize: number, 
    distortionPoints: Point[]
  ) => {
    // Calculate distortion effect for a given point
    const calculateDistortion = (x: number, y: number, distortionPoint: Point) => {
      // Convert normalized coordinates to canvas coordinates
      const distortX = distortionPoint.x * STANDARD_GRID_SIZE;
      const distortY = distortionPoint.y * STANDARD_GRID_SIZE;
      
      // Calculate distance from current point to distortion center
      const dx = x - distortX;
      const dy = y - distortY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Radius of influence - use stored radius or default
      const radius = distortionPoint.distortionRadius || 40;
      
      // Intensity of distortion - use stored intensity or default
      const intensity = distortionPoint.intensity || 0.8;
      
      // No effect beyond the radius
      if (distance > radius) return { x: 0, y: 0 };
      
      // Calculate distortion strength based on distance (stronger near center)
      const strength = (1 - distance / radius) * intensity * 12;
      
      // Direction from distortion point to current point
      const dirX = dx === 0 ? 0 : dx / Math.abs(dx);
      const dirY = dy === 0 ? 0 : dy / Math.abs(dy);
      
      // Create wave-like distortion
      return {
        x: dirX * strength * Math.sin(distance / 10),
        y: dirY * strength * Math.sin(distance / 10)
      };
    };
    
    // Draw horizontal grid lines with distortion
    ctx.lineWidth = 1.0;
    ctx.strokeStyle = '#666666'; // Gray color matching Android implementation
    
    for (let i = 0; i < gridLines; i++) {
      const y = Math.floor(i * cellSize) + 0.5;
      
      ctx.beginPath();
      
      // Start from left edge
      let prevX = 0;
      let prevY = y;
      ctx.moveTo(prevX, prevY);
      
      // Draw line with small segments to create curved effect
      const segments = 40;
      for (let j = 1; j <= segments; j++) {
        const x = (j / segments) * STANDARD_GRID_SIZE;
        
        // Start with base y position
        let newY = y;
        
        // Apply distortion from all distortion points
        distortionPoints.forEach(point => {
          const distortion = calculateDistortion(x, y, point);
          newY += distortion.y;
        });
        
        ctx.lineTo(x, newY);
        prevX = x;
        prevY = newY;
      }
      
      ctx.stroke();
    }
    
    // Draw vertical grid lines with distortion
    for (let i = 0; i < gridLines; i++) {
      const x = Math.floor(i * cellSize) + 0.5;
      
      ctx.beginPath();
      
      // Start from top edge
      let prevX = x;
      let prevY = 0;
      ctx.moveTo(prevX, prevY);
      
      // Draw line with small segments to create curved effect
      const segments = 40;
      for (let j = 1; j <= segments; j++) {
        const y = (j / segments) * STANDARD_GRID_SIZE;
        
        // Start with base x position
        let newX = x;
        
        // Apply distortion from all distortion points
        distortionPoints.forEach(point => {
          const distortion = calculateDistortion(x, y, point);
          newX += distortion.x;
        });
        
        ctx.lineTo(newX, y);
        prevX = newX;
        prevY = y;
      }
      
      ctx.stroke();
    }
  };
  
  // Draw the grid and marked points
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions to standard size
    canvas.width = STANDARD_GRID_SIZE;
    canvas.height = STANDARD_GRID_SIZE;
    
    // Clear the canvas
    ctx.clearRect(0, 0, STANDARD_GRID_SIZE, STANDARD_GRID_SIZE);
    
    // High-resolution Amsler grid is 20x20 (19 squares)
    const gridLines = 20; // 20 lines = 19 squares
    const cellSize = STANDARD_GRID_SIZE / 19; // Exact cell size for 19 squares
    
    // Draw white background for entire canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, STANDARD_GRID_SIZE, STANDARD_GRID_SIZE);
    
    // Draw distortion areas if there are marked points
    if (markedPoints.length > 0) {
      markedPoints.forEach(point => {
        // Only draw distortion area if intensity is defined
        if (point.intensity && point.distortionRadius) {
          const gradient = ctx.createRadialGradient(
            point.x * STANDARD_GRID_SIZE, 
            point.y * STANDARD_GRID_SIZE, 
            0,
            point.x * STANDARD_GRID_SIZE, 
            point.y * STANDARD_GRID_SIZE, 
            point.distortionRadius
          );
          
          gradient.addColorStop(0, 'rgba(255, 0, 0, 0.15)');
          gradient.addColorStop(0.7, 'rgba(255, 0, 0, 0.07)');
          gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(
            point.x * STANDARD_GRID_SIZE, 
            point.y * STANDARD_GRID_SIZE, 
            point.distortionRadius, 
            0, 
            Math.PI * 2
          );
          ctx.fill();
        }
      });
    }
    
    // Create distorted grid with current distortion points
    createDistortedGrid(
      ctx, 
      gridLines, 
      cellSize, 
      markedPoints.map(p => ({
        ...p,
        intensity: p.intensity || 0,
        distortionRadius: p.distortionRadius || 40
      }))
    );
    
    // Draw center dot
    ctx.fillStyle = 'black';
    ctx.beginPath();
    ctx.arc(STANDARD_GRID_SIZE / 2, STANDARD_GRID_SIZE / 2, 1.5, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw marked points
    ctx.fillStyle = 'red';
    markedPoints.forEach(point => {
      ctx.beginPath();
      ctx.arc(
        point.x * STANDARD_GRID_SIZE, 
        point.y * STANDARD_GRID_SIZE, 
        4, 
        0, 
        Math.PI * 2
      );
      ctx.fill();
    });
  }, [markedPoints, canvasRef]);
  
  // Handle animation for press-and-hold
  useEffect(() => {
    if (isPressing && pressPosition) {
      const animate = () => {
        const elapsedTime = Date.now() - pressStartTime;
        
        // Increase intensity more gradually over 3 seconds to maximum of 0.8
        const newIntensity = Math.min(0.8, elapsedTime / 3000);
        setCurrentIntensity(newIntensity);
        
        // Increase distortion radius gradually over 3 seconds to maximum of 40
        const newRadius = Math.min(40, (elapsedTime / 3000) * 40);
        setCurrentDistortionRadius(newRadius);
        
        // Continue animation if still pressing
        if (isPressing) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };
      
      animationFrameRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPressing, pressStartTime]);
  
  // Adjust mouse event handlers to get precise canvas coordinates
  const getCanvasCoordinates = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    // Calculate the ratio between the canvas element size and its internal size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Get precise coordinates within the canvas
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  };
  
  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getCanvasCoordinates(event.clientX, event.clientY);
    
    // Only start pressing if within the grid area
    if (x >= 0 && x <= STANDARD_GRID_SIZE && y >= 0 && y <= STANDARD_GRID_SIZE) {
      setIsPressing(true);
      setPressPosition({ x, y });
      setPressStartTime(Date.now());
      setCurrentIntensity(0);
      setCurrentDistortionRadius(0);
    }
  };
  
  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isPressing) return;
    
    const { x, y } = getCanvasCoordinates(event.clientX, event.clientY);
    
    // Update press position if still within grid
    if (x >= 0 && x <= STANDARD_GRID_SIZE && y >= 0 && y <= STANDARD_GRID_SIZE) {
      setPressPosition({ x, y });
    } else {
      // Cancel if moved outside grid
      setIsPressing(false);
      setPressPosition(null);
    }
  };
  
  const handleMouseUp = () => {
    if (isPressing && pressPosition && currentIntensity > 0.2) {
      // Only add a point if pressed long enough (intensity > 0.2)
      // Convert to normalized coordinates
      const normalizedX = pressPosition.x / STANDARD_GRID_SIZE;
      const normalizedY = pressPosition.y / STANDARD_GRID_SIZE;
      
      // Only add points that are within the grid area
      if (normalizedX >= 0 && normalizedX <= 1 && normalizedY >= 0 && normalizedY <= 1) {
        // Add the point with current intensity and distortion radius
        onMarkPoint({ 
          x: normalizedX, 
          y: normalizedY,
          intensity: currentIntensity,
          distortionRadius: currentDistortionRadius
        });
      }
    }
    
    // Reset press state
    setIsPressing(false);
    setPressPosition(null);
    setCurrentIntensity(0);
    setCurrentDistortionRadius(0);
  };
  
  const handleMouseLeave = () => {
    // Cancel press if mouse leaves canvas
    setIsPressing(false);
    setPressPosition(null);
    setCurrentIntensity(0);
    setCurrentDistortionRadius(0);
  };
  
  // Handle touch events for mobile devices with similar improvements
  const handleTouchStart = (event: React.TouchEvent<HTMLCanvasElement>) => {
    event.preventDefault(); // Prevent scrolling
    
    if (event.touches.length === 0) return;
    
    const touch = event.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);
    
    // Only start pressing if within the grid area
    if (x >= 0 && x <= STANDARD_GRID_SIZE && y >= 0 && y <= STANDARD_GRID_SIZE) {
      setIsPressing(true);
      setPressPosition({ x, y });
      setPressStartTime(Date.now());
      setCurrentIntensity(0);
      setCurrentDistortionRadius(0);
    }
  };
  
  const handleTouchMove = (event: React.TouchEvent<HTMLCanvasElement>) => {
    event.preventDefault(); // Prevent scrolling
    
    if (!isPressing || event.touches.length === 0) return;
    
    const touch = event.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);
    
    // Update press position if still within grid
    if (x >= 0 && x <= STANDARD_GRID_SIZE && y >= 0 && y <= STANDARD_GRID_SIZE) {
      setPressPosition({ x, y });
    } else {
      // Cancel if moved outside grid
      setIsPressing(false);
      setPressPosition(null);
    }
  };
  
  const handleTouchEnd = () => {
    if (isPressing && pressPosition && currentIntensity > 0.2) {
      // Only add a point if pressed long enough (intensity > 0.2)
      // Convert to normalized coordinates
      const normalizedX = pressPosition.x / STANDARD_GRID_SIZE;
      const normalizedY = pressPosition.y / STANDARD_GRID_SIZE;
      
      // Only add points that are within the grid area
      if (normalizedX >= 0 && normalizedX <= 1 && normalizedY >= 0 && normalizedY <= 1) {
        // Add the point with current intensity and distortion radius
        onMarkPoint({ 
          x: normalizedX, 
          y: normalizedY,
          intensity: currentIntensity,
          distortionRadius: currentDistortionRadius
        });
      }
    }
    
    // Reset press state
    setIsPressing(false);
    setPressPosition(null);
    setCurrentIntensity(0);
    setCurrentDistortionRadius(0);
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: 0,
        boxSizing: 'border-box',
        backgroundColor: '#ffffff' // Pure white background
      }}
    >
      {/* Eye test label placed above the canvas with proper spacing */}
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 1.5, // Adds margin bottom for spacing (12px by default in MUI)
          fontWeight: 500,
          textAlign: 'center',
          color: '#000000' // Ensure black text
        }}
      >
        {eye === 'left' ? 'Left' : 'Right'} Eye Test
      </Typography>
      
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{
          width: `${STANDARD_GRID_SIZE * canvasScale}px`,
          height: `${STANDARD_GRID_SIZE * canvasScale}px`,
          cursor: 'crosshair',
          boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.2)',
          border: '1px solid #ddd',
          touchAction: 'none', // Prevent scrolling on touch devices
          maxWidth: '98vmin', // Almost full viewport width on small screens
          maxHeight: '98vmin',
          marginBottom: '16px' // Add bottom margin to prevent cutoff
        }}
      />
    </Box>
  );
};

export default AmslerGridTest; 